var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ENVIRONMENT, getAwsConfig as getAwsConfigCore, getEnvironmentVariables as getEnvironmentVariablesCore } from '@amzn/austin-core';
import { getDayOfYear, getHours, getYear } from 'date-fns';
export var DOMAIN_MAPPINGS = [
    {
        EU: ENVIRONMENT.BETA_EU,
        FE: ENVIRONMENT.BETA_FE,
        NA: ENVIRONMENT.BETA_NA
    },
    {
        EU: ENVIRONMENT.GAMMA_EU,
        FE: ENVIRONMENT.GAMMA_FE,
        NA: ENVIRONMENT.GAMMA_NA
    },
    {
        EU: ENVIRONMENT.GAMMA_EU_AWS,
        FE: ENVIRONMENT.GAMMA_FE_AWS,
        NA: ENVIRONMENT.GAMMA_NA_AWS
    },
    {
        EU: ENVIRONMENT.PROD_EU,
        FE: ENVIRONMENT.PROD_FE,
        NA: ENVIRONMENT.PROD_NA
    },
    {
        EU: ENVIRONMENT.PROD_EU_AWS,
        FE: ENVIRONMENT.PROD_FE_AWS,
        NA: ENVIRONMENT.PROD_NA_AWS
    }
];
/**
 * IMPORTANT: Do not commit any changes to this configuration as it will break test/prod environments.
 *
 * This environment override config should be used for local development only. The purpose is to
 * override environment variables for testing local MFE applications and point to different (pre-prod)
 * AppSync endpoints, which is done by manipulating the `hostname` property. This config object will be
 * merged with the default environment config when getEnvironmentVariables() and getAwsConfig() is called.
 *
 * @example
 *  // First import the necessary mappings from our core library
 *  import { ENVIRONMENT, MICRO_FRONTENDS, RESOURCE_NAMESPACE } from '@amzn/austin-core';
 *
 *  // Point to locally running Actions and Tasks micro-frontends as well as the Beta NA AppSync endpoint.
 *  const environmentOverrides = {
 *      cognitoRedirect: 'http://localhost:8080',
 *      hostname: ENVIRONMENT.BETA_NA,
 *      mfeRemoteEntryOverrides: {
 *          [RESOURCE_NAMESPACE.CAM]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.CAM],
 *          [RESOURCE_NAMESPACE.TASKS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.TASKS]
 *      }
 *  };
 */
var environmentOverrides = {
    /**
     * @note
     * This tells Cognito where to redirect back to after authenticating and is ONLY used if the
     * hostname property has an override value. This should be left alone in most cases. DO NOT
     * change this value unless you need to specifically test redirecting to an environment other
     * than your locally running web application.
     */
    cognitoRedirect: 'http://localhost:8080',
    /**
     * @note
     * By default the environment is resolved by testing against `window.location.href` so that the
     * correct configuration can be loaded. For local dev testing, this will point to the Alpha AppSync
     * endpoint, however this can be overridden by passing in a different environment such as
     * ENVIRONMENT.BETA_NA, ENVIRONMENT.GAMMA_EU, etc.
     */
    hostname: '',
    /**
     * @note
     * This should be used for overridding the default remote entry point for micro-frontends when
     * doing local development. Simply uncomment one of the pre-defined, module-specific MFE endpoints
     * to load a micro-frontend from a local web server instead.
     */
    mfeRemoteEntryOverrides: {
    // [RESOURCE_NAMESPACE.CAM]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.CAM],
    // [RESOURCE_NAMESPACE.AUDITS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.AUDITS],
    // [RESOURCE_NAMESPACE.COMPLIANCE]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.COMPLIANCE],
    // [RESOURCE_NAMESPACE.DOC_REGISTRY]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.DOC_REGISTRY],
    // [RESOURCE_NAMESPACE.HOURS_WORKED]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.HOURS_WORKED],
    // [RESOURCE_NAMESPACE.INCIDENTS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.INCIDENTS],
    // [RESOURCE_NAMESPACE.INSPECTION]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.INSPECTION],
    // [RESOURCE_NAMESPACE.NETWORK_ALERTS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.NETWORK_ALERTS],
    // [RESOURCE_NAMESPACE.PERMISSIONS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.PERMISSIONS],
    // [RESOURCE_NAMESPACE.REGULATION_DISCOVERY]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.REGULATION_DISCOVERY],
    // [RESOURCE_NAMESPACE.REPORTS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.REPORTS],
    // [RESOURCE_NAMESPACE.REQUIREMENTS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.REQUIREMENTS],
    // [RESOURCE_NAMESPACE.RISK_ASSESSMENT]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.RISK_ASSESSMENT],
    // [RESOURCE_NAMESPACE.SITE_CONFIG]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.SITE_CONFIG],
    // [RESOURCE_NAMESPACE.TASKS]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.TASKS],
    // [RESOURCE_NAMESPACE.WASTE_DONATION]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.WASTE_DONATION],
    // [RESOURCE_NAMESPACE.WADM_SECURE_DOCUMENT]: MICRO_FRONTENDS.LOCAL[RESOURCE_NAMESPACE.WADM_SECURE_DOCUMENT]
    }
};
/**
 * Returns the appropriate environment config (MFE mappings, AppSync endpoint, etc.). Note that if the
 * `environmentOverrides.hostname` contains a value, then an overridden environment config is returned
 * rather than the default which resolves the environment by testing against `window.location.href`.
 */
export function getEnvironmentVariables() {
    var cognitoRedirect = environmentOverrides.cognitoRedirect, hostname = environmentOverrides.hostname;
    if (hostname) {
        return __assign(__assign({}, getEnvironmentVariablesCore(hostname)), { COGNITO_REDIRECT_SIGNIN: cognitoRedirect, COGNITO_REDIRECT_SIGNOUT: cognitoRedirect });
    }
    return getEnvironmentVariablesCore();
}
/**
 * Returns the appropriate AWS config that gets passed to Amplify.configure() when the application is
 * initializing. Note that if the `environmentOverrides.hostname` contains a value, then an overridden
 * AWS config is returned rather than the default which resolves the environment by testing against
 * `window.location.href`.
 */
export function getAwsConfig() {
    var cognitoRedirect = environmentOverrides.cognitoRedirect, hostname = environmentOverrides.hostname;
    if (hostname) {
        var config = getAwsConfigCore(hostname);
        config.Auth.oauth.redirectSignIn = cognitoRedirect;
        config.Auth.oauth.redirectSignOut = cognitoRedirect;
        return config;
    }
    return getAwsConfigCore();
}
/**
 * Helper function to return a unique hour-day id to end of remote entry file url to invalidate browser cache
 * Set as default param to getMicroFrontendConfig
 *
 * @todo Ideally the cache string would be set by each module on publishishing of a new version.
 * investigate how to get a new MFE bundle version from a MFE deployment
 *
 * @returns {string} cacheString
 * queryParam string built from current date hour + day e.g. ts=10360
 */
function getMFECacheVersion() {
    var date = new Date();
    var cacheString = "ts=".concat(getHours(date), "-").concat(getDayOfYear(date)).concat(getYear(date));
    return cacheString;
}
/**
 * Returns the configuration for a Microfrontend module per given environment
 *
 * @param {string} moduleName
 * The name of the configured module name defined in DEFAULT_ENVIRONMENT_CONFIG
 * MFE remoteEntry url will be stored in memory on first initialization.  Subsequent
 *
 * @param {boolean} [useCache=true]
 * Flag to use in memory cache when fetching MFE config.
 *
 * @param {Function} [getCacheString=getMFECacheVersion]
 * (Optional) function that can be passed to determine queryParm to add to end of remote entry file url to invalidate browser cache
 * Defaults to getMFECacheVersion, which returns an arbitrary timestamp
 *
 * @returns {string} remoteEntry
 * Microfrontend remoteEntry.js url for the environment
 */
var moduleRemoteEntry = {};
export function getMicroFrontendConfig(moduleName, useCache, getCacheString) {
    if (useCache === void 0) { useCache = true; }
    if (getCacheString === void 0) { getCacheString = getMFECacheVersion; }
    var microFrontendVariables = getEnvironmentVariables()
        .MICRO_FRONTENDS;
    var mfeConfig = __assign(__assign({}, microFrontendVariables), environmentOverrides.mfeRemoteEntryOverrides);
    var remoteEntry = '';
    if (useCache && moduleRemoteEntry[moduleName]) {
        // If the remoteEntry has already been initialized for the MFE, use the cached URL
        remoteEntry = moduleRemoteEntry[moduleName];
    }
    else {
        // add unique hour-day id to end of remote entry file url to invalidate browser cache
        var cacheString = getCacheString();
        if (mfeConfig[moduleName]) {
            remoteEntry = "".concat(mfeConfig[moduleName], "?").concat(cacheString);
        }
        // set the remoteEntry to be cached for the module
        if (useCache) {
            moduleRemoteEntry[moduleName] = remoteEntry;
        }
    }
    return remoteEntry;
}
/**
 * Determines if current environment is alpha or not.
 *
 * @return {Boolean} returns true if in a alpha environment; false otherwise.
 */
export function isAlpha() {
    return [ENVIRONMENT.ALPHA].includes(window.location.hostname);
}
/**
 * Determines if current environment is beta or not.
 *
 * @return {Boolean} returns true if in a beta environment; false otherwise.
 */
export function isBeta() {
    var BETA_EU = ENVIRONMENT.BETA_EU, BETA_FE = ENVIRONMENT.BETA_FE, BETA_NA = ENVIRONMENT.BETA_NA;
    return [BETA_EU, BETA_FE, BETA_NA].includes(window.location.hostname);
}
/**
 * Determines if current environment is gamma or not.
 *
 * @return {Boolean} returns true if in a gamma environment; false otherwise.
 */
export function isGamma() {
    var GAMMA_EU = ENVIRONMENT.GAMMA_EU, GAMMA_EU_AWS = ENVIRONMENT.GAMMA_EU_AWS, GAMMA_FE = ENVIRONMENT.GAMMA_FE, GAMMA_FE_AWS = ENVIRONMENT.GAMMA_FE_AWS, GAMMA_NA = ENVIRONMENT.GAMMA_NA, GAMMA_NA_AWS = ENVIRONMENT.GAMMA_NA_AWS;
    return [
        GAMMA_EU,
        GAMMA_EU_AWS,
        GAMMA_FE,
        GAMMA_FE_AWS,
        GAMMA_NA,
        GAMMA_NA_AWS
    ].includes(window.location.hostname);
}
/**
 * Tests if request is coming from the local/dev environment.
 *
 * @returns {Boolean} isLocalhost Returns true if on local dev environment; false otherwise.
 */
export function isLocalhost() {
    var hostname = window.location.hostname;
    return Boolean(hostname === 'localhost' ||
        // This will be the hostname if accessing from a VirtualBox VM
        hostname === '10.0.2.2' ||
        // [::1] is the IPv6 localhost address.
        hostname === '[::1]' ||
        // 127.0.0.1/8 is considered localhost for IPv4.
        hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/));
}
/**
 * Determines if current environment is prod or not.
 *
 * @return {Boolean} returns true if in a prod environment; false otherwise.
 */
export function isProd() {
    return [
        ENVIRONMENT.PROD_NA,
        ENVIRONMENT.PROD_NA_AWS,
        ENVIRONMENT.PROD_FE,
        ENVIRONMENT.PROD_FE_AWS,
        ENVIRONMENT.PROD_EU,
        ENVIRONMENT.PROD_EU_AWS,
        ENVIRONMENT.PROD_FE,
        ENVIRONMENT.PROD_FE_AWS
    ].includes(window.location.hostname);
}
